import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Edit2, Trash2, Upload, Search, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { categoriesApi, examsApi } from '../../services/api';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  examId: string;
  questionCount: number;
  order: number;
  isActive: boolean;
  exam?: { name: string };
}

interface Exam {
  id: string;
  name: string;
}

type SortKey = 'name' | 'exam' | 'questionCount' | 'order' | 'isActive';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 25;

export function CategoriesList() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const examFilter = searchParams.get('exam') || '';
  const searchQuery = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const sortKey = (searchParams.get('sort') || 'name') as SortKey;
  const sortDir = (searchParams.get('dir') || 'asc') as SortDir;

  useEffect(() => {
    examsApi.getAll().then((res) => {
      setExams(Array.isArray(res.data) ? res.data : res.data.items ?? []);
    });
  }, []);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await categoriesApi.getAll({
        examId: examFilter || undefined,
        includeInactive: true,
        page,
        limit: PAGE_SIZE,
        search: searchQuery || undefined,
        sort: sortKey,
        dir: sortDir,
      });
      if (data.items) {
        setCategories(data.items);
        setTotal(data.total);
      } else {
        const arr = Array.isArray(data) ? data : [];
        setCategories(arr);
        setTotal(arr.length);
      }
    } catch {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  }, [examFilter, page, searchQuery, sortKey, sortDir]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const safePage = Math.max(1, Math.min(page, totalPages || 1));

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== 'page') next.set('page', '1');
    setSearchParams(next);
  };

  const handleSearchChange = (value: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set('q', value);
    } else {
      next.delete('q');
    }
    next.set('page', '1');
    searchTimerRef.current = setTimeout(() => {
      setSearchParams(next);
    }, 300);
  };

  const toggleSort = (key: SortKey) => {
    const next = new URLSearchParams(searchParams);
    if (sortKey === key) {
      next.set('dir', sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sort', key);
      next.set('dir', 'asc');
    }
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (p: number) => updateParam('page', p.toString());

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Supprimer la catégorie "${name}" ?`)) return;
    try {
      await categoriesApi.delete(id);
      toast.success('Catégorie supprimée');
      loadCategories();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={13} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />;
    return sortDir === 'asc' ? <ArrowUp size={13} className="text-emerald-500" /> : <ArrowDown size={13} className="text-emerald-500" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Catégories</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {total} catégorie{total !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/categories/import"
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
          >
            <Upload size={16} />
            Importer
          </Link>
          <Link
            to={`/admin/categories/new${examFilter ? `?exam=${examFilter}` : ''}`}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm"
          >
            <Plus size={16} />
            Nouvelle catégorie
          </Link>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        {/* Search + Filter bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher une catégorie..."
                defaultValue={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Examen :</label>
            <select
              value={examFilter}
              onChange={(e) => updateParam('exam', e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900 dark:text-white"
            >
              <option value="">Tous les examens</option>
              {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <p className="text-base font-medium">
              {searchQuery ? 'Aucun résultat pour cette recherche' : 'Aucune catégorie pour l\'instant'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('name')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Catégorie <SortIcon col="name" />
                  </button>
                </th>
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('exam')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Examen <SortIcon col="exam" />
                  </button>
                </th>
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('questionCount')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Questions <SortIcon col="questionCount" />
                  </button>
                </th>
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('order')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Ordre <SortIcon col="order" />
                  </button>
                </th>
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('isActive')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Statut <SortIcon col="isActive" />
                  </button>
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {categories.map((cat) => (
                <tr key={cat.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xl">{cat.icon || '📖'}</span>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{cat.name}</p>
                        <p className="text-xs text-gray-400">{cat.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                    {cat.exam?.name ?? '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{cat.questionCount}</td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{cat.order}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      cat.isActive
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/categories/${cat.id}`}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title="Modifier"
                      >
                        <Edit2 size={16} />
                      </Link>
                      <button
                        onClick={() => handleDelete(cat.id, cat.name)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {safePage} sur {totalPages} — {total} résultat{total !== 1 ? 's' : ''}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 1}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= totalPages}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
